import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, setAdminToken } from "@/lib/api";
import { STATUS_FLOW, PAYMENT_STATUSES } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import StatusStepper from "@/components/StatusStepper";
import AdminLayout from "@/components/AdminLayout";
import { ArrowLeft, Loader2, Save, Trash2, Phone, Mail, Calendar, AlertTriangle, Send, MessageSquarePlus, Camera, X, Receipt, FileText, Download, ShieldCheck, Unlock, FileCheck2, ImageIcon, CheckCircle2, ClipboardCheck, RotateCcw } from "lucide-react";
import QuotePicker from "@/components/QuotePicker";
import JobChecklist from "@/components/JobChecklist";
import { JobDiscountCard } from "@/components/JobDiscountCard";
import { JobDepositCard } from "@/components/JobDepositCard";
import { toast } from "sonner";

const AdminJobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [timelineMsg, setTimelineMsg] = useState("");
  const [timelineFiles, setTimelineFiles] = useState([]); // [{file, uploading, media?, error?}]
  const [postingTimeline, setPostingTimeline] = useState(false);
  const [quoteItems, setQuoteItems] = useState([]);
  const [savingQuote, setSavingQuote] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [confirmDeleteJob, setConfirmDeleteJob] = useState(false);
  const [confirmCancelJob, setConfirmCancelJob] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [confirmRestoreJob, setConfirmRestoreJob] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteUpdate, setConfirmDeleteUpdate] = useState(null); // updateId or null

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/jobs/${id}`);
      setJob(res.data);
      setNotFound(false);
      setEdits({
        status: res.data.status,
        estimated_price: res.data.estimated_price ?? "",
        final_price: res.data.final_price ?? "",
        payment_status: res.data.payment_status,
        amount_paid: res.data.amount_paid ?? "",
        internal_notes: res.data.internal_notes ?? "",
      });
      setQuoteItems(res.data.quote_items || []);
    } catch (err) {
      if (err.response?.status === 401) {
        setAdminToken(null);
        navigate("/admin/login");
      } else if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error("Failed to load job");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: edits.status,
        payment_status: edits.payment_status,
        amount_paid: edits.amount_paid === "" ? null : Number(edits.amount_paid),
        internal_notes: edits.internal_notes,
        // estimated_price is now derived from quote items — backend ignores any value here.
        final_price: edits.final_price === "" ? null : Number(edits.final_price),
      };
      const res = await api.patch(`/admin/jobs/${id}`, payload);
      setJob(res.data);
      toast.success("Saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteJob = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success("Job deleted");
      navigate("/admin/jobs");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
      setConfirmDeleteJob(false);
    }
  };

  const cancelJob = async () => {
    setCancelling(true);
    try {
      const res = await api.post(`/admin/jobs/${id}/cancel`, { reason: cancelReason });
      setJob(res.data);
      toast.success("Job cancelled — customer has been emailed.");
      setConfirmCancelJob(false);
      setCancelReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  const restoreJob = async () => {
    setRestoring(true);
    try {
      const res = await api.post(`/admin/jobs/${id}/restore`);
      setJob(res.data);
      setEdits((e) => ({ ...e, status: res.data.status }));
      toast.success(`Job restored to "${res.data.status}" — customer has been emailed.`);
      setConfirmRestoreJob(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const postTimeline = async (e) => {
    e.preventDefault();
    const msg = timelineMsg.trim();
    const uploadedMedia = timelineFiles.filter((f) => f.media).map((f) => f.media);
    if (!msg && uploadedMedia.length === 0) {
      toast.error("Add a message or at least one photo");
      return;
    }
    if (timelineFiles.some((f) => f.uploading)) {
      toast.error("Wait for uploads to finish");
      return;
    }
    setPostingTimeline(true);
    try {
      const res = await api.post(`/admin/jobs/${id}/timeline`, { message: msg, media: uploadedMedia });
      setJob(res.data);
      setTimelineMsg("");
      setTimelineFiles([]);
      toast.success("Update posted to customer tracker");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Post failed");
    } finally {
      setPostingTimeline(false);
    }
  };

  const handleTimelineFiles = async (fileList) => {
    const arr = Array.from(fileList);
    const queued = arr.map((f) => ({ file: f, uploading: true, media: null, error: null }));
    setTimelineFiles((s) => [...s, ...queued]);
    for (const item of queued) {
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setTimelineFiles((s) => s.map((it) => (it.file === item.file ? { ...it, uploading: false, media: res.data } : it)));
      } catch (err) {
        setTimelineFiles((s) => s.map((it) => (it.file === item.file ? { ...it, uploading: false, error: err.response?.data?.detail || "Upload failed" } : it)));
        toast.error(`Upload failed: ${item.file.name}`);
      }
    }
  };

  const removeTimelineFile = (file) => setTimelineFiles((s) => s.filter((it) => it.file !== file));

  const removeTimeline = async (updateId) => {
    try {
      const res = await api.delete(`/admin/jobs/${id}/timeline/${updateId}`);
      setJob(res.data);
      toast.success("Update removed");
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      setConfirmDeleteUpdate(null);
    }
  };

  const saveQuote = async () => {
    setSavingQuote(true);
    try {
      const res = await api.patch(`/admin/jobs/${id}`, { quote_items: quoteItems });
      setJob(res.data);
      setQuoteItems(res.data.quote_items || []);
      setEdits((s) => ({
        ...s,
        estimated_price: res.data.estimated_price ?? "",
      }));
      toast.success(`Quote saved · estimate set to $${(res.data.quoted_total ?? 0).toFixed(2)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingQuote(false);
    }
  };

  const quoteTotal = quoteItems.reduce((sum, v) => sum + Number(v.price || 0), 0);

  const approveByAdmin = async () => {
    const note = window.prompt("How was this approved? (e.g. 'phone call', 'in person', 'email reply')", "phone call");
    if (note === null) return; // cancelled
    setApprovalBusy(true);
    try {
      const res = await api.post(`/admin/jobs/${id}/quote/approve`, { note });
      setJob(res.data);
      toast.success("Quote approved & locked. Customer notified.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    } finally {
      setApprovalBusy(false);
    }
  };

  const unlockQuote = async () => {
    if (!confirm("Unlock the quote? This allows further edits but the approval record is preserved.")) return;
    setApprovalBusy(true);
    try {
      const res = await api.post(`/admin/jobs/${id}/quote/unlock`);
      setJob(res.data);
      toast.success("Quote unlocked");
    } catch (err) {
      toast.error("Unlock failed");
    } finally {
      setApprovalBusy(false);
    }
  };

  const generatePdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.post(`/admin/jobs/${id}/quote/pdf`);
      setJob(res.data);
      toast.success("PDF generated and stored");
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF generation failed");
    } finally {
      setPdfBusy(false);
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/admin/jobs/${id}/quote/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Spark-Quote-${job.ref_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    } finally {
      setPdfBusy(false);
    }
  };

  const viewPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/admin/jobs/${id}/quote/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error("Open failed");
    } finally {
      setPdfBusy(false);
    }
  };

  const openDoc = async (kind) => {
    // Pre-open the tab synchronously so popup blockers don't kill it after the await.
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/admin/jobs/${id}/${kind}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        // Popup was blocked — fall back to current-tab navigation
        window.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win && !win.closed) win.close();
      toast.error(err.response?.data?.detail || `Failed to open ${kind}`);
    }
  };

  const downloadDoc = async (kind) => {
    try {
      const res = await api.get(`/admin/jobs/${id}/${kind}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Spark-${kind.charAt(0).toUpperCase()}${kind.slice(1)}-${job.ref_number}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      // Slight delay before cleanup so Safari/iOS can complete the download
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 1000);
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const emailDoc = async (kind) => {
    if (!confirm(`Email ${kind} to ${job.email}?`)) return;
    try {
      await api.post(`/admin/jobs/${id}/${kind}/email`);
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} emailed to ${job.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email failed");
    }
  };

  const verifyProof = async (proofId, markPaid = true) => {
    try {
      const res = await api.post(`/admin/jobs/${id}/payment-proof/${proofId}/verify`, null, { params: { mark_paid: markPaid } });
      setJob(res.data);
      toast.success(markPaid ? "Verified & marked Paid · receipt emailed" : "Marked as verified");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verify failed");
    }
  };

  const deleteProof = async (proofId) => {
    try {
      const res = await api.delete(`/admin/jobs/${id}/payment-proof/${proofId}`);
      setJob(res.data);
      toast.success("Removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  if (notFound) {
    return (
      <AdminLayout title="Job not found">
        <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] py-16 text-center max-w-xl mx-auto" data-testid="job-not-found">
          <AlertTriangle className="size-10 text-spark-yellow mx-auto mb-3" />
          <p className="text-slate-200 font-display uppercase tracking-wider text-lg">This job no longer exists</p>
          <p className="text-sm text-slate-400 mt-1">It might have been deleted, or the link is from a stale notification.</p>
          <Link to="/admin/jobs" className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-spark-yellow-dark">
            <ArrowLeft className="size-3.5" /> Back to jobs
          </Link>
        </div>
      </AdminLayout>
    );
  }

  if (loading || !job) {
    return (
      <AdminLayout title="Loading…">
        <div className="text-center py-20 text-slate-400"><Loader2 className="size-6 animate-spin inline" /></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      eyebrow={`Job · ${job.ref_number}`}
      title={job.full_name}
      actions={
        <>
          <Link to="/admin/jobs" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-white/5 border border-white/10 text-xs uppercase tracking-wider font-semibold text-slate-200 hover:bg-white/10">
            <ArrowLeft className="size-3.5" /> All jobs
          </Link>
          {job?.status !== "Cancelled" && (
            <button onClick={() => setConfirmCancelJob(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-slate-500/10 border border-slate-400/30 text-xs uppercase tracking-wider font-bold text-slate-300 hover:bg-slate-500/20" data-testid="cancel-job-btn">
              <X className="size-3.5" /> Cancel job
            </button>
          )}
          {job?.status === "Cancelled" && (
            <button onClick={() => setConfirmRestoreJob(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-emerald-500/10 border border-emerald-400/30 text-xs uppercase tracking-wider font-bold text-emerald-300 hover:bg-emerald-500/20" data-testid="restore-job-btn">
              <RotateCcw className="size-3.5" /> Restore job
            </button>
          )}
          <button onClick={() => setConfirmDeleteJob(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-xs uppercase tracking-wider font-bold text-red-400 hover:bg-red-500/20" data-testid="delete-job-btn">
            <Trash2 className="size-3.5" /> Delete
          </button>
        </>
      }
    >
      <div className="bg-spark-floor text-spark-ink rounded-xl border border-spark-line p-4 sm:p-6 lg:p-8 overflow-hidden" data-testid="admin-job-detail">
        <main className="grid lg:grid-cols-12 gap-6">
        {/* Left: customer + issue */}
        <section className="lg:col-span-7 space-y-6 min-w-0">
          <div className="surface-card p-6 sm:p-8">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
              <div className="min-w-0">
                <p className="label-mono text-spark-teal mb-1">Customer</p>
                <h1 className="font-display uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl font-bold break-words">{job.full_name}</h1>
              </div>
              <StatusBadge status={job.status} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <a href={`tel:${job.phone}`} className="flex items-center gap-2.5 hover:text-spark-teal min-w-0" data-testid="customer-phone">
                <Phone className="size-4 text-spark-teal shrink-0" /> <span className="truncate">{job.phone}</span>
              </a>
              <a href={`mailto:${job.email}`} className="flex items-center gap-2.5 hover:text-spark-teal min-w-0" data-testid="customer-email">
                <Mail className="size-4 text-spark-teal shrink-0" /> <span className="truncate">{job.email}</span>
              </a>
              <div className="flex items-center gap-2.5 min-w-0">
                <Calendar className="size-4 text-spark-teal shrink-0" />
                <span className="truncate">{job.pickup_required ? "Pickup time" : "Drop-off"}: {new Date(job.preferred_dropoff_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}</span>
              </div>
              <div className="flex items-center gap-2.5">
                <AlertTriangle className="size-4 text-spark-teal" />
                Urgency: <span className="capitalize font-semibold">{job.urgency}</span>
              </div>
            </div>
          </div>

          <div className="surface-card p-6 sm:p-8">
            <p className="label-mono text-spark-teal mb-4">Scooter & Issue</p>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div><dt className="label-mono mb-1">Brand</dt><dd className="font-semibold">{job.scooter_brand}</dd></div>
              <div><dt className="label-mono mb-1">Model</dt><dd className="font-semibold">{job.scooter_model}</dd></div>
              <div><dt className="label-mono mb-1">Rideable</dt><dd className="font-semibold">{job.is_rideable ? "Yes" : "No"}</dd></div>
              <div><dt className="label-mono mb-1">Service</dt><dd className="font-semibold capitalize">{job.service_type}</dd></div>
              {/* Pickup / Return — admin can add/remove at any time after booking */}
              <LogisticsEditor job={job} onSaved={(j) => { setJob(j); }} />
              <div className="sm:col-span-2"><dt className="label-mono mb-1">Issue Started</dt><dd>{job.issue_started}</dd></div>
              <div className="sm:col-span-2"><dt className="label-mono mb-1">Issue Description</dt><dd className="whitespace-pre-wrap leading-relaxed">{job.issue_description}</dd></div>
            </dl>
          </div>

          {/* Quote Builder */}
          <div className="surface-card p-6 sm:p-8" data-testid="quote-builder">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <p className="label-mono text-spark-teal flex items-center gap-2"><Receipt className="size-3.5" /> Quote Builder</p>
                <p className="text-xs text-spark-slate mt-0.5">Pick services and parts you'll need. Total syncs to the customer's quote automatically.</p>
              </div>
              <div className="text-right">
                <p className="label-mono">Current total</p>
                <p className="font-display uppercase tracking-tight text-2xl font-bold text-spark-ink" data-testid="quote-builder-total">${quoteTotal.toFixed(2)}</p>
              </div>
            </div>

            {job.quote_locked && (
              <div className="bg-spark-yellow text-spark-ink rounded-md p-4 mb-4 flex items-start gap-3" data-testid="quote-locked-banner">
                <ShieldCheck className="size-6 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-display uppercase tracking-tight font-bold text-base">Quote approved & locked</p>
                  <p className="text-xs mt-0.5">
                    Approved by <strong>{job.quote_approved_by}</strong> on {new Date(job.quote_approved_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                  <p className="text-xs mt-0.5 italic">Quote items and PDF are frozen. Unlock if scope must change.</p>
                </div>
                <button onClick={unlockQuote} disabled={approvalBusy} className="btn-outline-ink !py-1.5 !px-3 text-xs" data-testid="unlock-quote-btn">
                  {approvalBusy ? <Loader2 className="size-3.5 animate-spin" /> : <Unlock className="size-3.5" />} Unlock
                </button>
              </div>
            )}

            <div className={job.quote_locked ? "opacity-60 pointer-events-none" : ""}>
              <QuotePicker value={quoteItems} onChange={setQuoteItems} tone="dark" />
            </div>
            <div className="flex items-center justify-end mt-4 gap-2 flex-wrap">
              {job.quote_items?.length > 0 && JSON.stringify(job.quote_items) !== JSON.stringify(quoteItems) && !job.quote_locked && (
                <span className="label-mono text-amber-600 mr-auto">Unsaved changes</span>
              )}
              {!job.quote_locked && (
                <button onClick={saveQuote} disabled={savingQuote} className="btn-primary" data-testid="save-quote-btn">
                  {savingQuote ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save Quote
                </button>
              )}
              {!job.quote_locked && job.quote_items?.length > 0 && (
                <button onClick={approveByAdmin} disabled={approvalBusy} className="btn-accent" data-testid="approve-quote-btn">
                  {approvalBusy ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />} Mark Approved
                </button>
              )}
            </div>

            {/* PDF actions */}
            <div className="mt-5 pt-5 border-t border-spark-line">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                  <p className="label-mono text-spark-teal flex items-center gap-2">
                    <FileText className="size-3.5" /> Quote PDF
                  </p>
                  {job.quote_pdf?.generated_at ? (
                    <p className="text-xs text-spark-slate mt-0.5">
                      Last generated: {new Date(job.quote_pdf.generated_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                      {" · "}
                      {(job.quote_pdf.size / 1024 / 1024).toFixed(2)} MB stored
                    </p>
                  ) : (
                    <p className="text-xs text-spark-slate mt-0.5">No PDF yet — generate to attach a branded quote document.</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={generatePdf} disabled={pdfBusy || quoteItems.length === 0 || job.quote_locked} className="btn-accent !py-2 !px-4 text-xs" data-testid="generate-pdf-btn" title={job.quote_locked ? "Quote is locked" : (quoteItems.length === 0 ? "Add at least one service first" : "Generate fresh PDF")}>
                  {pdfBusy ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />} {job.quote_pdf ? "Regenerate PDF" : "Generate PDF"}
                </button>
                {job.quote_pdf?.storage_path && (
                  <>
                    <button onClick={viewPdf} disabled={pdfBusy} className="btn-outline-ink !py-2 !px-4 text-xs" data-testid="view-pdf-btn">
                      <Receipt className="size-4" /> View
                    </button>
                    <button onClick={downloadPdf} disabled={pdfBusy} className="btn-outline-ink !py-2 !px-4 text-xs" data-testid="download-pdf-btn">
                      <Download className="size-4" /> Download
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Media */}
          {job.media?.length > 0 && (
            <div className="surface-card p-6 sm:p-8">
              <p className="label-mono text-spark-teal mb-4">Customer Photos / Videos · {job.media.length}</p>
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="media-gallery">
                {job.media.map((m) => {
                  const isVideo = m.content_type?.startsWith("video/");
                  const url = fileUrl(m.storage_path);
                  return (
                    <li key={m.id} className="relative aspect-square overflow-hidden rounded-md border border-spark-line bg-spark-floor">
                      <a href={url} target="_blank" rel="noreferrer" className="block w-full h-full">
                        {isVideo ? (
                          <video src={url} className="w-full h-full object-cover" muted />
                        ) : (
                          <img src={url} alt={m.original_filename} className="w-full h-full object-cover" />
                        )}
                      </a>
                      <div className="absolute inset-x-0 bottom-0 bg-spark-teal-deep/85 text-white text-[10px] px-2 py-1 truncate">
                        {m.original_filename}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="surface-card p-6 sm:p-8">
            <p className="label-mono text-spark-teal mb-4">Workflow tracker</p>
            <StatusStepper currentStatus={job.status} history={job.status_history} />
          </div>

          <JobChecklist job={job} onJobUpdated={setJob} />
        </section>

        {/* Right: editable */}
        <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start space-y-6 min-w-0">
          {/* Documents */}
          <div className="surface-card p-6 sm:p-7" data-testid="documents-card">
            <p className="label-mono text-spark-teal mb-4 flex items-center gap-2"><FileText className="size-3.5" /> Documents</p>
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-spark-line p-3.5 flex items-center justify-between gap-3">
                <div>
                  <p className="font-display uppercase tracking-wider font-bold text-spark-ink">Quote</p>
                  <p className="text-xs text-spark-slate">Branded PDF for customer approval.</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => openDoc("quote")} className="px-2.5 py-1.5 rounded-md border border-spark-line hover:border-spark-teal text-xs font-bold uppercase tracking-wider" title="Open in new tab" data-testid="doc-quote-open">Open</button>
                  <button onClick={() => downloadDoc("quote")} className="px-2.5 py-1.5 rounded-md border border-spark-line hover:border-spark-teal text-xs font-bold uppercase tracking-wider" title="Download" data-testid="doc-quote-download"><Download className="size-3.5" /></button>
                </div>
              </div>
              <div className="rounded-md border border-spark-line p-3.5 flex items-center justify-between gap-3">
                <div>
                  <p className="font-display uppercase tracking-wider font-bold text-spark-ink">Invoice</p>
                  <p className="text-xs text-spark-slate">Includes bank transfer details &amp; GST breakdown.</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => openDoc("invoice")} className="px-2.5 py-1.5 rounded-md border border-spark-line hover:border-spark-teal text-xs font-bold uppercase tracking-wider" data-testid="doc-invoice-open">Open</button>
                  <button onClick={() => downloadDoc("invoice")} className="px-2.5 py-1.5 rounded-md border border-spark-line hover:border-spark-teal text-xs font-bold uppercase tracking-wider"><Download className="size-3.5" /></button>
                  <button onClick={() => emailDoc("invoice")} className="px-2.5 py-1.5 rounded-md bg-spark-teal text-white hover:bg-spark-teal-dark text-xs font-bold uppercase tracking-wider" title={`Email to ${job.email}`} data-testid="doc-invoice-email"><Send className="size-3.5" /></button>
                </div>
              </div>
              <div className={`rounded-md border p-3.5 flex items-center justify-between gap-3 ${job.payment_status === "Paid" ? "border-spark-line" : "border-spark-line bg-spark-floor opacity-70"}`}>
                <div>
                  <p className="font-display uppercase tracking-wider font-bold text-spark-ink">Receipt</p>
                  <p className="text-xs text-spark-slate">{job.payment_status === "Paid" ? "Tax receipt for paid jobs." : "Mark job as Paid to enable."}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => openDoc("receipt")} disabled={job.payment_status !== "Paid"} className="px-2.5 py-1.5 rounded-md border border-spark-line hover:border-spark-teal text-xs font-bold uppercase tracking-wider disabled:opacity-30 disabled:hover:border-spark-line" data-testid="doc-receipt-open">Open</button>
                  <button onClick={() => downloadDoc("receipt")} disabled={job.payment_status !== "Paid"} className="px-2.5 py-1.5 rounded-md border border-spark-line hover:border-spark-teal text-xs font-bold uppercase tracking-wider disabled:opacity-30"><Download className="size-3.5" /></button>
                  <button onClick={() => emailDoc("receipt")} disabled={job.payment_status !== "Paid"} className="px-2.5 py-1.5 rounded-md bg-spark-teal text-white hover:bg-spark-teal-dark text-xs font-bold uppercase tracking-wider disabled:opacity-30" data-testid="doc-receipt-email"><Send className="size-3.5" /></button>
                </div>
              </div>
              {job.checklist?.completed_at && (
                <div className="rounded-md border border-spark-line p-3.5 flex items-center justify-between gap-3" data-testid="doc-checklist-row">
                  <div className="min-w-0">
                    <p className="font-display uppercase tracking-wider font-bold text-spark-ink flex items-center gap-1.5"><ClipboardCheck className="size-3.5 text-spark-teal" /> Service Checklist</p>
                    <p className="text-xs text-spark-slate truncate">{job.checklist.template_name} · {job.checklist.outcome}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => openDoc("checklist")} className="px-2.5 py-1.5 rounded-md border border-spark-line hover:border-spark-teal text-xs font-bold uppercase tracking-wider" data-testid="doc-checklist-open">Open</button>
                    <button onClick={() => downloadDoc("checklist")} className="px-2.5 py-1.5 rounded-md border border-spark-line hover:border-spark-teal text-xs font-bold uppercase tracking-wider" data-testid="doc-checklist-download"><Download className="size-3.5" /></button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {!!(job.payment_proofs && job.payment_proofs.length) && (
            <div className="surface-card p-6 sm:p-7" data-testid="payment-proofs-card">
              <div className="flex items-center justify-between mb-4">
                <p className="label-mono text-spark-teal flex items-center gap-2"><FileCheck2 className="size-3.5" /> Payment proofs ({job.payment_proofs.length})</p>
                {job.payment_proofs.some((p) => !p.verified) && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-[10px] uppercase tracking-wider font-bold">Needs review</span>
                )}
              </div>
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {job.payment_proofs.map((p) => {
                  const url = fileUrl(p.storage_path);
                  const isImg = (p.content_type || "").startsWith("image/");
                  return (
                    <li key={p.id} className="rounded-md border border-spark-line overflow-hidden bg-spark-floor">
                      <a href={url} target="_blank" rel="noreferrer" className="block aspect-square">
                        {isImg
                          ? <img src={url} alt="Payment proof" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center"><FileCheck2 className="size-8 text-spark-teal" /></div>}
                      </a>
                      <div className="p-2 space-y-1.5">
                        <p className="text-[10px] text-spark-slate uppercase tracking-wider">{new Date(p.uploaded_at).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })}</p>
                        {p.note && <p className="text-xs text-spark-ink italic line-clamp-2" title={p.note}>"{p.note}"</p>}
                        <div className="flex flex-col gap-1">
                          {!p.verified ? (
                            <>
                              <button onClick={() => verifyProof(p.id, true)} className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-sm bg-emerald-500 text-white text-[10px] uppercase tracking-wider font-bold hover:bg-emerald-600" title="Verify proof and auto-mark job as Paid (sends receipt)" data-testid={`verify-proof-${p.id}`}>
                                <CheckCircle2 className="size-3" /> Verify &amp; mark Paid
                              </button>
                              <div className="flex gap-1">
                                <button onClick={() => verifyProof(p.id, false)} className="flex-1 px-2 py-0.5 rounded-sm text-[9px] text-spark-slate hover:text-spark-teal uppercase tracking-wider" title="Just mark proof as verified — don't change payment status" data-testid={`verify-only-${p.id}`}>
                                  Verify only
                                </button>
                                <button onClick={() => deleteProof(p.id)} className="px-2 py-0.5 rounded-sm text-spark-slate hover:text-red-500" title="Remove" data-testid={`delete-proof-${p.id}`}>
                                  <Trash2 className="size-3" />
                                </button>
                              </div>
                            </>
                          ) : (
                            <div className="flex gap-1">
                              <span className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-sm bg-emerald-100 text-emerald-800 text-[10px] uppercase tracking-wider font-bold"><CheckCircle2 className="size-3" /> Verified</span>
                              <button onClick={() => deleteProof(p.id)} className="px-2 py-1 rounded-sm text-spark-slate hover:text-red-500 hover:bg-red-50" title="Remove" data-testid={`delete-proof-${p.id}`}>
                                <Trash2 className="size-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[11px] text-spark-slate italic mt-3"><strong>Verify &amp; mark Paid</strong> closes the loop in one click — flips payment status to Paid and auto-emails the customer their tax receipt. Use <em>Verify only</em> if you want to confirm the screenshot looks right without changing payment status yet.</p>
            </div>
          )}

          <div className="surface-card p-6 sm:p-8">
            <p className="label-mono text-spark-teal mb-4">Manage job</p>
            <div className="space-y-5">
              <div>
                <label className="label-mono mb-2 inline-block">Status</label>
                <select className="input-style" value={edits.status} onChange={(e) => setEdits((s) => ({ ...s, status: e.target.value }))} data-testid="edit-status">
                  {STATUS_FLOW.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-mono mb-2 inline-block">Estimated price <span className="text-spark-slate text-[10px] normal-case tracking-normal">(auto from quote items)</span></label>
                  <div className="input-style bg-spark-floor flex items-center justify-between gap-2 cursor-not-allowed select-none" data-testid="edit-estimated-display">
                    <span className="font-display text-spark-ink">${Number(job.quoted_total || 0).toFixed(2)}</span>
                    <span className="text-[10px] uppercase tracking-wider text-spark-slate">{(job.quote_items || []).length} item{(job.quote_items || []).length === 1 ? "" : "s"}</span>
                  </div>
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Final price</label>
                  <input type="number" min={0} step="0.01" className="input-style" placeholder="0.00" value={edits.final_price} onChange={(e) => setEdits((s) => ({ ...s, final_price: e.target.value }))} data-testid="edit-final" />
                </div>
              </div>
              <div>
                <label className="label-mono mb-2 inline-block">Payment status</label>
                <div className="grid grid-cols-3 gap-2">
                  {PAYMENT_STATUSES.map((p) => (
                    <button key={p} type="button" onClick={() => setEdits((s) => ({ ...s, payment_status: p }))} className={`label-mono py-2 rounded-sm border transition ${edits.payment_status === p ? "bg-spark-teal-deep text-white border-spark-ink" : "border-spark-line text-spark-slate hover:border-spark-ink"}`} data-testid={`edit-payment-${p.toLowerCase()}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-mono mb-2 inline-block">Amount paid so far</label>
                <input type="number" min={0} step="0.01" className="input-style" placeholder="0.00" value={edits.amount_paid} onChange={(e) => setEdits((s) => ({ ...s, amount_paid: e.target.value }))} data-testid="edit-amount-paid" />
                <p className="text-xs text-spark-slate mt-1.5">
                  Set whatever's been paid so far. If it covers the total, the job auto-flips to <strong>Paid</strong> + emails the receipt.
                  {(() => {
                    const total = Number(edits.final_price || job.quoted_total || 0);
                    const paid = Number(edits.amount_paid || 0);
                    if (total <= 0) return null;
                    const bal = Math.max(0, total - paid);
                    return <span className="block mt-1"><span className="font-bold">Balance owing:</span> <span className="font-mono">${bal.toFixed(2)}</span></span>;
                  })()}
                </p>
              </div>
              <div>
                <label className="label-mono mb-2 inline-block">Internal notes</label>
                <textarea rows={4} className="input-style resize-none" placeholder="Parts ordered, customer comments, etc." value={edits.internal_notes} onChange={(e) => setEdits((s) => ({ ...s, internal_notes: e.target.value }))} data-testid="edit-notes" />
              </div>
              <button onClick={save} disabled={saving} className="btn-primary w-full" data-testid="save-job-btn">
                {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save Changes
              </button>
            </div>
          </div>

          {/* Customer-visible timeline updates */}
          <div className="surface-card p-6 sm:p-8" data-testid="timeline-panel">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquarePlus className="size-4 text-spark-teal" />
              <p className="label-mono text-spark-teal">Customer Timeline</p>
            </div>
            <p className="text-xs text-spark-slate mb-4">
              Posted updates appear on the customer's public tracker page. Use it for "Parts ordered today", "Test ride passed", etc.
            </p>
            <form onSubmit={postTimeline} className="space-y-3">
              <textarea
                rows={3}
                className="input-style resize-none"
                placeholder="e.g. Battery arrived this morning — fitting it tomorrow."
                value={timelineMsg}
                onChange={(e) => setTimelineMsg(e.target.value)}
                data-testid="timeline-input"
                maxLength={500}
              />
              {timelineFiles.length > 0 && (
                <ul className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="timeline-upload-preview">
                  {timelineFiles.map((it, i) => (
                    <li key={i} className="relative aspect-square rounded-md overflow-hidden border border-spark-line bg-spark-floor">
                      {it.file.type.startsWith("image/") ? (
                        <img src={URL.createObjectURL(it.file)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-spark-teal-deep text-spark-yellow"><Camera className="size-6" /></div>
                      )}
                      <button type="button" onClick={() => removeTimelineFile(it.file)} className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-0.5" data-testid={`remove-timeline-file-${i}`}>
                        <X className="size-3" />
                      </button>
                      <div className="absolute inset-x-0 bottom-0 bg-spark-teal-deep/85 text-white text-[9px] px-1 py-0.5">
                        {it.uploading ? "Uploading…" : it.error ? "✗ " + it.error : "✓"}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1.5 label-mono text-spark-teal hover:text-spark-teal-dark cursor-pointer" data-testid="timeline-upload-label">
                  <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => handleTimelineFiles(e.target.files)} data-testid="timeline-upload-input" />
                  <Camera className="size-4" /> Add photos
                </label>
                <span className="text-xs text-spark-slate">{timelineMsg.length}/500</span>
                <button type="submit" disabled={postingTimeline || (!timelineMsg.trim() && timelineFiles.filter((f) => f.media).length === 0)} className="btn-accent !py-2 !px-4 text-xs" data-testid="post-timeline-btn">
                  {postingTimeline ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} Post Update
                </button>
              </div>
            </form>

            {job.timeline_updates?.length > 0 && (
              <ul className="mt-5 pt-5 border-t border-spark-line space-y-3" data-testid="timeline-list">
                {[...job.timeline_updates].reverse().map((u) => (
                  <li key={u.id} className="bg-spark-floor border border-spark-line rounded-md p-3 group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        {u.message && <p className="text-sm text-spark-ink leading-relaxed whitespace-pre-wrap">{u.message}</p>}
                        {u.media?.length > 0 && (
                          <ul className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                            {u.media.map((m) => {
                              const url = fileUrl(m.storage_path);
                              const isVideo = m.content_type?.startsWith("video/");
                              return (
                                <li key={m.id} className="aspect-square rounded-sm overflow-hidden border border-spark-line">
                                  <a href={url} target="_blank" rel="noreferrer" className="block w-full h-full">
                                    {isVideo ? <video src={url} muted className="w-full h-full object-cover" /> : <img src={url} alt="" className="w-full h-full object-cover" />}
                                  </a>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                      <button onClick={() => setConfirmDeleteUpdate(u.id)} className="opacity-0 group-hover:opacity-100 text-spark-slate hover:text-red-500 transition shrink-0" title="Delete update" data-testid={`delete-timeline-${u.id}`}>
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                    <p className="text-xs text-spark-slate mt-2">
                      {new Date(u.posted_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {(!job.timeline_updates || job.timeline_updates.length === 0) && (
              <p className="mt-5 pt-5 border-t border-spark-line text-xs text-spark-slate italic">No customer-facing updates posted yet.</p>
            )}
          </div>

          <JobDepositCard job={job} onUpdated={setJob} />

          <JobDiscountCard job={job} onUpdated={(j) => { setJob(j); setQuoteItems(j.quote_items || []); }} />

          <div className="surface-card p-6 text-xs text-spark-slate space-y-1.5">
            <p><span className="label-mono">Created:</span> {new Date(job.created_at).toLocaleString("en-NZ")}</p>
            <p><span className="label-mono">Last update:</span> {new Date(job.updated_at).toLocaleString("en-NZ")}</p>
            <p data-testid="job-terms-accepted">
              <span className="label-mono">T&Cs:</span>{" "}
              {job.terms_version
                ? <>Agreed to v{job.terms_version} on {new Date(job.terms_accepted_at).toLocaleString("en-NZ")}</>
                : <span className="italic">Not recorded (admin entry or pre-dates T&C tickbox)</span>}
            </p>
            <p><span className="label-mono">Job ID:</span> <code className="text-[10px]">{job.id}</code></p>
          </div>
        </aside>
        </main>
      </div>

      {confirmCancelJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="confirm-cancel-job-modal" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-xl border border-slate-400/30 bg-spark-ink shadow-2xl overflow-hidden">
            <div className="bg-slate-500/10 border-b border-slate-400/30 px-6 py-4 flex items-center gap-3">
              <X className="size-5 text-slate-300" />
              <h2 className="font-display uppercase tracking-wider text-base font-bold text-slate-200">Cancel this job?</h2>
            </div>
            <div className="px-6 py-5 text-sm text-slate-300 space-y-3">
              <p>You&apos;re about to cancel <strong className="text-white">{job.ref_number}</strong> — {job.full_name}.</p>
              <p className="text-slate-400">The job stays in the system and history is preserved — the status just changes to <strong className="text-white">Cancelled</strong>. The customer will get a confirmation email at <strong className="text-white">{job.email}</strong>.</p>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 inline-block">Reason (optional, shown in the email)</label>
                <textarea
                  rows={2}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. Customer changed their mind, out-of-scope repair, etc."
                  className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-none"
                  data-testid="cancel-job-reason"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10 flex items-center justify-end gap-2">
              <button onClick={() => { setConfirmCancelJob(false); setCancelReason(""); }} disabled={cancelling} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-300 hover:bg-white/5 disabled:opacity-50" data-testid="confirm-cancel-job-cancel">
                Never mind
              </button>
              <button onClick={cancelJob} disabled={cancelling} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-500 text-white text-xs uppercase tracking-wider font-bold hover:bg-slate-600 disabled:opacity-60" data-testid="confirm-cancel-job-confirm">
                {cancelling ? <Loader2 className="size-3.5 animate-spin" /> : <X className="size-3.5" />}
                {cancelling ? "Cancelling…" : "Cancel job"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmRestoreJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="confirm-restore-job-modal" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-xl border border-emerald-400/30 bg-spark-ink shadow-2xl overflow-hidden">
            <div className="bg-emerald-500/10 border-b border-emerald-400/30 px-6 py-4 flex items-center gap-3">
              <RotateCcw className="size-5 text-emerald-300" />
              <h2 className="font-display uppercase tracking-wider text-base font-bold text-emerald-200">Restore this job?</h2>
            </div>
            <div className="px-6 py-5 text-sm text-slate-300 space-y-3">
              <p>Reactivate <strong className="text-white">{job.ref_number}</strong> — {job.full_name}?</p>
              <p className="text-slate-400">The job will be reverted to its <strong className="text-white">last known status</strong> before it was cancelled (or <strong className="text-white">Received</strong> if we can&apos;t tell). Nothing is lost — quote, checklist, timeline and history all stay put.</p>
              <p className="text-slate-400">A confirmation email will be sent to <strong className="text-white">{job.email}</strong>.</p>
            </div>
            <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmRestoreJob(false)} disabled={restoring} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-300 hover:bg-white/5 disabled:opacity-50" data-testid="confirm-restore-job-cancel">
                Never mind
              </button>
              <button onClick={restoreJob} disabled={restoring} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 text-white text-xs uppercase tracking-wider font-bold hover:bg-emerald-600 disabled:opacity-60" data-testid="confirm-restore-job-confirm">
                {restoring ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" />}
                {restoring ? "Restoring…" : "Restore job"}
              </button>
            </div>
          </div>
        </div>
      )}


      {confirmDeleteJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="confirm-delete-job-modal" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-xl border border-red-500/30 bg-spark-ink shadow-2xl overflow-hidden">
            <div className="bg-red-500/10 border-b border-red-500/30 px-6 py-4 flex items-center gap-3">
              <AlertTriangle className="size-5 text-red-400" />
              <h2 className="font-display uppercase tracking-wider text-base font-bold text-red-300">Delete this job?</h2>
            </div>
            <div className="px-6 py-5 text-sm text-slate-300 space-y-2">
              <p>You're about to permanently delete <strong className="text-white">{job.ref_number}</strong> — {job.full_name}.</p>
              <p className="text-slate-400">This cannot be undone. Customer record, timeline updates and quote history will all be removed.</p>
            </div>
            <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDeleteJob(false)} disabled={deleting} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-300 hover:bg-white/5 disabled:opacity-50" data-testid="confirm-delete-job-cancel">
                Cancel
              </button>
              <button onClick={deleteJob} disabled={deleting} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-500 text-white text-xs uppercase tracking-wider font-bold hover:bg-red-600 disabled:opacity-60" data-testid="confirm-delete-job-confirm">
                {deleting ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
                {deleting ? "Deleting…" : "Delete job"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteUpdate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="confirm-delete-update-modal" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-xl border border-red-500/30 bg-spark-ink shadow-2xl overflow-hidden">
            <div className="bg-red-500/10 border-b border-red-500/30 px-6 py-4 flex items-center gap-3">
              <AlertTriangle className="size-5 text-red-400" />
              <h2 className="font-display uppercase tracking-wider text-base font-bold text-red-300">Delete timeline update?</h2>
            </div>
            <div className="px-6 py-5 text-sm text-slate-300">
              <p>This update will be removed from the customer-facing tracker.</p>
            </div>
            <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDeleteUpdate(null)} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-300 hover:bg-white/5" data-testid="confirm-delete-update-cancel">
                Cancel
              </button>
              <button onClick={() => removeTimeline(confirmDeleteUpdate)} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-500 text-white text-xs uppercase tracking-wider font-bold hover:bg-red-600" data-testid="confirm-delete-update-confirm">
                <Trash2 className="size-3.5" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminJobDetail;

/**
 * Pickup / Return editor — admin can add or remove the logistics services
 * after a booking is created. Backend keeps quote_items in sync so the
 * customer's quote/total automatically reflects the change.
 */
const LogisticsEditor = ({ job, onSaved }) => {
  const [pickup, setPickup] = useState(!!job.pickup_required);
  const [dropoff, setDropoff] = useState(!!job.dropoff_required);
  const [address, setAddress] = useState(job.service_address || "");
  const [saving, setSaving] = useState(false);

  // Re-sync state when job updates from outside (e.g. after another save).
  useEffect(() => {
    setPickup(!!job.pickup_required);
    setDropoff(!!job.dropoff_required);
    setAddress(job.service_address || "");
  }, [job.pickup_required, job.dropoff_required, job.service_address]);

  const dirty = pickup !== !!job.pickup_required || dropoff !== !!job.dropoff_required || (address || "") !== (job.service_address || "");
  const needsAddress = pickup || dropoff;

  const save = async () => {
    if (needsAddress && !address.trim()) {
      toast.error("Address required when pickup or return is enabled");
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/admin/jobs/${job.id}`, {
        pickup_required: pickup,
        dropoff_required: dropoff,
        service_address: needsAddress ? address.trim() : null,
      });
      onSaved?.(res.data);
      toast.success("Pickup / return updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save logistics");
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ checked, onChange, label, hint, testid }) => (
    <label className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition ${checked ? "bg-spark-teal-light border-spark-teal" : "bg-white border-spark-line hover:border-spark-ink"}`} data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 size-4 accent-spark-teal" />
      <span className="flex-1 text-xs">
        <strong className="block font-display uppercase tracking-tight text-spark-ink">{label}</strong>
        <span className="text-spark-slate">{hint}</span>
      </span>
      <span className="font-display uppercase tracking-tight font-bold text-spark-ink text-xs">+$40</span>
    </label>
  );

  return (
    <div className="sm:col-span-2 pt-4 border-t border-spark-line" data-testid="job-logistics-editor">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <dt className="label-mono">Pickup / Return delivery</dt>
        {(job.pickup_required || job.dropoff_required) && (
          <span className="text-[10px] uppercase tracking-wider text-spark-slate font-bold">Currently: {[job.pickup_required && "Pickup", job.dropoff_required && "Return"].filter(Boolean).join(" + ") || "—"}</span>
        )}
      </div>
      <p className="text-[11px] text-spark-slate mb-3">Customers can add these themselves at booking, or you can toggle them on/off here. Adding ${pickup && dropoff ? 80 : 40} to the quote automatically.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Toggle checked={pickup} onChange={setPickup} label="Pickup from customer" hint="We collect their scooter from their address." testid="toggle-pickup" />
        <Toggle checked={dropoff} onChange={setDropoff} label="Return to customer" hint="We deliver back once it's ready." testid="toggle-dropoff" />
      </div>
      {needsAddress && (
        <div className="mt-3">
          <label className="label-mono mb-1.5 inline-block">Pickup / return address</label>
          <textarea
            rows={2}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Street, suburb, city"
            className="input-style resize-none text-sm"
            data-testid="logistics-address"
          />
        </div>
      )}
      {dirty && (
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary mt-3 text-xs"
          data-testid="save-logistics-btn"
        >
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
          Save pickup / return
        </button>
      )}
    </div>
  );
};
